import { format, isValid } from 'date-fns';
import isNaN from 'lodash/isNaN';

export const parseText = (value: string) => value || null;

export const parseNumberFilter = (
  value: string,
): string | number | undefined => {
  const numString = value.replace(/\D/g, '');

  const number = numString ? parseInt(numString, 10) : undefined;

  return isNaN(number) ? undefined : number;
};

export const parseDate = (dateString: string): Date | undefined => {
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? undefined : date;
};

/**
 * Formats a date to "4:00PM on May 14th, 2025" format
 * @param date - Date object or string to format
 * @returns Formatted date string or empty string if invalid date
 */
export const formatDateTime = (date: Date | string): string => {
  // Convert string to Date if needed
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Return empty string if invalid date
  if (!isValid(dateObj)) {
    return '';
  }

  // Get the day to add the correct suffix
  const day = dateObj.getDate();

  // Add suffix to day (1st, 2nd, 3rd, 4th, etc.)
  let daySuffix = 'th';
  if (day % 10 === 1 && day !== 11) daySuffix = 'st';
  else if (day % 10 === 2 && day !== 12) daySuffix = 'nd';
  else if (day % 10 === 3 && day !== 13) daySuffix = 'rd';

  // Format the time and date parts
  const timeFormat = format(dateObj, 'h:mmaaa');
  const dateFormat = format(dateObj, `MMM d'${daySuffix}', yyyy`);

  return `${timeFormat} on ${dateFormat}`;
};

/**
 * Converts a time string (e.g., "14:00") into a localized time string.
 * @param time - The time string in "HH:mm" format.
 * @returns A localized time string (e.g., "2:00 PM").
 */
export const formatTime = (time: string): string => {
  if (!time) return '';

  try {
    return new Date(`1970-01-01T${time}`).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    console.error('Invalid time format:', time);
    return '';
  }
};
